<script lang="ts">
  /**
   * The flat category search on filter line 2 (spec 6.3, #2247). One searchable list of the
   * categories present in the exposure + provider view, grouped like the SDD page's "Filter by
   * Category" dropdown: Custom Types, then Built-in Categories, alphabetical, Uncategorised last.
   * Each option is a tick, the category chip (its tooltip lists the display names of its raw
   * names) and the document count.
   *
   * Built on the app's `command` + `popover` primitives, like `identity/ContextSwitcher.svelte`:
   * the command gives option roles and arrow-key/Enter navigation, the popover gives Escape and
   * outside-click closing. The view model does the searching, so the command's filter is off.
   *
   * Ticking applies at once through `onChange` (no draft, no Apply). A partial category shows an
   * indeterminate tick; ticking it selects the whole category. The list is closed on mount and
   * opens on click or typing, never on focus alone.
   */
  import * as Command from "$ui/command";
  import * as Popover from "$ui/popover";
  import CategoryChip from "../CategoryChip.svelte";
  import {
    provisionalSuffix,
    type ActiveInfoTypeSelection,
    type ExposedSensitiveDocument,
  } from "../exposedSensitiveDocuments";
  import type { NamedCustomInfoType } from "../categoryLabels";
  import {
    buildCategoryList,
    categoryState,
    docsLabel,
    searchCategories,
    toggleCategory,
    type CategoryRow,
    type TriState,
  } from "./categorySearch";

  interface Props {
    /** The exposure + provider view, NOT trimmed to the active list (counts ignore what is ticked). */
    rows: ExposedSensitiveDocument[];
    /** The active raw names, or `null` for show-all. */
    active: string[] | null;
    /** The org's enabled custom info types. */
    customInfoTypes?: NamedCustomInfoType[];
    /** The rows are still loading, so each count is only the rows so far (#2265). */
    provisional?: boolean;
    /** Called on every tick or untick with the new URL selection. */
    onChange: (selection: ActiveInfoTypeSelection) => void;
  }

  let { rows, active, customInfoTypes = [], provisional = false, onChange }: Props = $props();

  let query = $state("");
  let open = $state(false);

  const list = $derived(buildCategoryList(rows, { active, customInfoTypes }));
  const groups = $derived(searchCategories(list.groups, query));

  const ARIA_CHECKED: Record<TriState, "true" | "false" | "mixed"> = { all: "true", some: "mixed", none: "false" };

  function toggle(row: CategoryRow) {
    onChange(toggleCategory(row, active));
  }
</script>

<Command.Root
  shouldFilter={false}
  label="Categories"
  class="relative w-[28rem] max-w-full overflow-visible bg-transparent"
>
  <!-- portal={null}: cmdk finds its items under its root node, so a list portalled to <body> breaks arrow keys.
       disableFocusTrap: the trap would move focus into the list on open; focus must stay in the input while typing. -->
  <Popover.Root
    bind:open
    disableFocusTrap
    portal={null}
    openFocus={(trigger?: HTMLElement | null) => trigger?.querySelector("input") ?? null}
    closeFocus={() => null}
    let:ids
  >
    <!-- The input is the popover's anchor, not a toggle button: clicking or typing opens, so it
         carries the trigger id (outside-click ignores it) without the trigger's toggle action. -->
    <div
      id={ids.trigger}
      class="field-input rounded-md focus-within:border-primary [&_[data-cmdk-input-wrapper]]:border-b-0"
      data-testid="category-search"
    >
      <!-- The forms plugin borders and rings every bare input; the wrapper is the field here. -->
      <Command.Input
        bind:value={query}
        class="h-9 border-0 focus:ring-0"
        placeholder="Search categories, e.g. passport, tax, credit card"
        aria-label="Categories"
        aria-expanded={open}
        onclick={() => (open = true)}
        oninput={() => (open = true)}
        onkeydown={(event: KeyboardEvent) => {
          // The popover's own Escape handling only fires for focus inside its content, and focus
          // stays in this input, so the input closes the list itself.
          if (event.key === "Escape") open = false;
        }}
      />
    </div>
    <Popover.Content sameWidth sideOffset={4} class="z-30 w-auto p-0">
      <Command.List aria-label="Categories" class="max-h-96">
        {#each groups as group (group.name)}
          <Command.Group heading={group.name}>
            {#each group.categories as row (row.category)}
              {@const state = categoryState(row, active)}
              <Command.Item
                value={row.category}
                onSelect={() => toggle(row)}
                aria-checked={ARIA_CHECKED[state]}
                class="gap-2 text-sm"
                data-testid="category-search-option"
                data-category={row.category}
              >
                <!-- A visual tick only: the option itself carries aria-checked and takes the click. -->
                <input
                  type="checkbox"
                  class="pointer-events-none accent-primary"
                  tabindex="-1"
                  aria-hidden="true"
                  checked={state === "all"}
                  indeterminate={state === "some"}
                />
                <span class="min-w-0 flex-1">
                  <CategoryChip
                    label={row.label}
                    color={row.color}
                    class="inline-block max-w-full truncate align-middle"
                    title={row.displayNames.join("\n")}
                    data-testid="category-search-chip"
                  />
                </span>
                <span class="text-xs tabular-nums text-gray-500" data-testid="category-search-count">
                  {docsLabel(row.docs)}{provisionalSuffix(provisional)}
                </span>
              </Command.Item>
            {/each}
          </Command.Group>
        {:else}
          <Command.Empty class="px-3 py-3 text-left text-gray-500">No categories match "{query}"</Command.Empty>
        {/each}
      </Command.List>
    </Popover.Content>
  </Popover.Root>
</Command.Root>
